/**
 * Database Health Monitor Service
 *
 * This service monitors IndexedDB health, detects corruption,
 * and provides automatic recovery mechanisms to prevent the
 * database from becoming invisible or unusable.
 */

import { isFeatureEnabled } from '../config/featureFlags';
import { db } from '../db';
import Dexie from 'dexie';
import eventBus from '../utils/eventBus';

class DatabaseHealthMonitor {
    constructor() {
        this.lastHealthCheck = 0;
        this.healthCheckInterval = 30000; // 30 seconds
        this.corruptionDetected = false;
        this.recoveryInProgress = false;
        this.healthStatus = 'unknown';
        this.lastError = null;
        this.consecutiveErrors = 0;
        this.maxConsecutiveErrors = 3;

        // Health check metrics
        this.metrics = {
            totalHealthChecks: 0,
            failedHealthChecks: 0,
            corruptionEvents: 0,
            recoveryAttempts: 0,
            successfulRecoveries: 0,
            averageCheckTime: 0
        };

        // Recovery strategies
        this.recoveryStrategies = [
            'reconnect',
            'clearCorruptedData',
            'recreateDatabase'
        ];

        console.log('[DatabaseHealthMonitor] Initialized (inactive until feature flag enabled)');
    }

    /**
     * Check database health
     * @returns {Promise<boolean>} True if database is healthy
     */
    async checkHealth() {
        if (!isFeatureEnabled('USE_HEALTH_MONITOR')) {
            return true; // Assume healthy if monitoring disabled
        }

        const now = Date.now();
        if (now - this.lastHealthCheck < this.healthCheckInterval) {
            return this.healthStatus === 'healthy';
        }

        const startTime = Date.now();
        this.metrics.totalHealthChecks++;

        try {
            if (isFeatureEnabled('ENABLE_DEBUG_LOGGING')) {
                console.log('[DatabaseHealthMonitor] Starting health check...');
            }

            // Test basic database connectivity
            await this.testDatabaseConnectivity();

            // Test table integrity
            await this.testTableIntegrity();

            // Test transaction capability
            await this.testTransactionCapability();

            // Check for corruption indicators
            await this.detectCorruption();

            // Update health status
            this.healthStatus = 'healthy';
            this.consecutiveErrors = 0;
            this.lastError = null;
            this.lastHealthCheck = now;

            // Update metrics
            const checkTime = Date.now() - startTime;
            this.updateCheckTimeMetrics(checkTime);

            if (isFeatureEnabled('ENABLE_DEBUG_LOGGING')) {
                console.log(`[DatabaseHealthMonitor] Health check passed (${checkTime}ms)`);
            }

            return true;

        } catch (error) {
            this.handleHealthCheckError(error);
            this.metrics.failedHealthChecks++;

            const checkTime = Date.now() - startTime;
            this.updateCheckTimeMetrics(checkTime);

            return false;
        }
    }

    /**
     * Test basic database connectivity
     */
    async testDatabaseConnectivity() {
        try {
            // Test if database can be opened
            if (!db.isOpen()) {
                await db.open();
            }

            // Test basic read operation
            await db.transaction('r', db.images, async () => {
                // Just test that we can start a transaction
            });

        } catch (error) {
            throw new DatabaseError('Connectivity test failed', error);
        }
    }

    /**
     * Test table integrity
     */
    async testTableIntegrity() {
        const requiredTables = ['images', 'collection', 'settings'];

        for (const tableName of requiredTables) {
            try {
                const table = db[tableName];
                if (!table) {
                    throw new Error(`Table ${tableName} not found`);
                }

                // Test basic table operations
                await table.limit(1).toArray();

            } catch (error) {
                throw new DatabaseError(`Table integrity test failed for ${tableName}`, error);
            }
        }
    }

    /**
     * Test transaction capability
     */
    async testTransactionCapability() {
        try {
            await db.transaction('rw', db.settings, async () => {
                // Test that we can perform read/write transactions
                const testKey = '_health_check_test';
                const testValue = Date.now();

                await db.settings.put({ key: testKey, value: testValue });
                const result = await db.settings.where('key').equals(testKey).first();

                if (!result || result.value !== testValue) {
                    throw new Error('Transaction verification failed');
                }

                // Clean up test data
                await db.settings.where('key').equals(testKey).delete();
            });

        } catch (error) {
            throw new DatabaseError('Transaction capability test failed', error);
        }
    }

    /**
     * Detect corruption indicators
     */
    async detectCorruption() {
        const corruptionTests = [
            () => this.testImageTableCorruption(),
            () => this.testCollectionTableCorruption(),
            () => this.testIndexCorruption()
        ];

        for (const test of corruptionTests) {
            try {
                await test();
            } catch (error) {
                throw new DatabaseCorruptionError('Corruption detected', error);
            }
        }
    }

    /**
     * Test for corruption in images table
     */
    async testImageTableCorruption() {
        try {
            // Test count operation
            const count = await db.images.count();

            // Test index operations
            await db.images.orderBy('id').limit(1).toArray();
            await db.images.orderBy('uid').limit(1).toArray();

            // Test filter operations
            await db.images.where('id').above(0).limit(1).toArray();

        } catch (error) {
            throw new Error(`Images table corruption: ${error.message}`);
        }
    }

    /**
     * Test for corruption in collection table
     */
    async testCollectionTableCorruption() {
        try {
            await db.collection.count();
            await db.collection.orderBy('id').limit(1).toArray();

        } catch (error) {
            throw new Error(`Collection table corruption: ${error.message}`);
        }
    }

    /**
     * Test for index corruption
     */
    async testIndexCorruption() {
        try {
            // Test various index operations
            await db.images.where('hash').equals('test').count();
            await db.images.where('uid').equals('test').count();

        } catch (error) {
            throw new Error(`Index corruption: ${error.message}`);
        }
    }

    /**
     * Handle health check errors
     * @param {Error} error - The error that occurred
     */
    handleHealthCheckError(error) {
        this.consecutiveErrors++;
        this.lastError = error;
        this.healthStatus = 'unhealthy';

        console.error('[DatabaseHealthMonitor] Health check failed:', error);

        if (error instanceof DatabaseCorruptionError) {
            this.handleCorruption(error);
        } else if (this.consecutiveErrors >= this.maxConsecutiveErrors) {
            console.warn(`[DatabaseHealthMonitor] ${this.consecutiveErrors} consecutive errors, attempting recovery`);
            this.attemptRecovery();
        }
    }

    /**
     * Handle detected corruption
     * @param {Error} error - The corruption error
     */
    async handleCorruption(error) {
        console.error('[DatabaseHealthMonitor] Corruption detected:', error);

        this.corruptionDetected = true;
        this.metrics.corruptionEvents++;

        // Emit corruption event
        eventBus.emit('database_corruption_detected', {
            error: error.message,
            timestamp: Date.now()
        });

        // Show user notification if enabled
        if (isFeatureEnabled('ENABLE_CORRUPTION_ALERTS')) {
            this.showCorruptionAlert();
        }

        // Attempt automatic recovery if enabled
        if (isFeatureEnabled('ENABLE_AUTOMATIC_RECOVERY')) {
            await this.attemptRecovery();
        }
    }

    /**
     * Attempt to recover from database issues
     */
    async attemptRecovery() {
        if (this.recoveryInProgress) {
            console.log('[DatabaseHealthMonitor] Recovery already in progress');
            return;
        }

        this.recoveryInProgress = true;
        this.metrics.recoveryAttempts++;

        console.log('[DatabaseHealthMonitor] Starting recovery process...');

        try {
            for (const strategy of this.recoveryStrategies) {
                console.log(`[DatabaseHealthMonitor] Trying recovery strategy: ${strategy}`);

                const success = await this.executeRecoveryStrategy(strategy);
                if (success) {
                    console.log(`[DatabaseHealthMonitor] Recovery successful with strategy: ${strategy}`);
                    this.metrics.successfulRecoveries++;
                    this.corruptionDetected = false;
                    this.consecutiveErrors = 0;
                    this.healthStatus = 'healthy';
                    break;
                }
            }

        } catch (error) {
            console.error('[DatabaseHealthMonitor] Recovery failed:', error);
        } finally {
            this.recoveryInProgress = false;
        }
    }

    /**
     * Execute a specific recovery strategy
     * @param {string} strategy - The recovery strategy to execute
     * @returns {Promise<boolean>} True if recovery was successful
     */
    async executeRecoveryStrategy(strategy) {
        try {
            switch (strategy) {
                case 'reconnect':
                    return await this.reconnectDatabase();

                case 'clearCorruptedData':
                    return await this.clearCorruptedData();

                case 'recreateDatabase':
                    return await this.recreateDatabase();

                default:
                    console.warn(`[DatabaseHealthMonitor] Unknown recovery strategy: ${strategy}`);
                    return false;
            }
        } catch (error) {
            console.error(`[DatabaseHealthMonitor] Recovery strategy ${strategy} failed:`, error);
            return false;
        }
    }

    /**
     * Attempt to reconnect to the database
     * @returns {Promise<boolean>} True if reconnection was successful
     */
    async reconnectDatabase() {
        try {
            if (db.isOpen()) {
                db.close();
            }

            await db.open();
            await this.testDatabaseConnectivity();

            console.log('[DatabaseHealthMonitor] Database reconnection successful');
            return true;

        } catch (error) {
            console.error('[DatabaseHealthMonitor] Database reconnection failed:', error);
            return false;
        }
    }

    /**
     * Clear corrupted data from the database
     * @returns {Promise<boolean>} True if cleanup was successful
     */
    async clearCorruptedData() {
        try {
            // This is a placeholder for actual corrupted data cleanup logic
            // In practice, this would identify and remove specific corrupted records

            console.log('[DatabaseHealthMonitor] Corrupted data cleanup - not implemented yet');
            return false;

        } catch (error) {
            console.error('[DatabaseHealthMonitor] Corrupted data cleanup failed:', error);
            return false;
        }
    }

    /**
     * Recreate the database from scratch
     * @returns {Promise<boolean>} True if recreation was successful
     */
    async recreateDatabase() {
        try {
            // Backup existing data if possible
            let backupData = null;
            if (isFeatureEnabled('ENABLE_BACKUP_ON_CORRUPTION')) {
                backupData = await this.backupDatabase();
            }

            // Close and delete the database
            if (db.isOpen()) {
                db.close();
            }

            await Dexie.delete('promptus-pwa-db');

            // Wait a moment for cleanup
            await new Promise(resolve => setTimeout(resolve, 1000));

            // Recreate the database
            await db.open();

            // Restore data if backup was successful
            if (backupData) {
                await this.restoreDatabase(backupData);
            }

            console.log('[DatabaseHealthMonitor] Database recreation successful');
            return true;

        } catch (error) {
            console.error('[DatabaseHealthMonitor] Database recreation failed:', error);
            return false;
        }
    }

    /**
     * Backup database data before corruption recovery
     * @returns {Promise<object|null>} Backup data or null if failed
     */
    async backupDatabase() {
        try {
            const backup = {
                timestamp: Date.now(),
                images: [],
                collection: [],
                settings: []
            };

            // Try to backup each table
            try {
                backup.images = await db.images.toArray();
            } catch (error) {
                console.warn('[DatabaseHealthMonitor] Could not backup images table:', error);
            }

            try {
                backup.collection = await db.collection.toArray();
            } catch (error) {
                console.warn('[DatabaseHealthMonitor] Could not backup collection table:', error);
            }

            try {
                backup.settings = await db.settings.toArray();
            } catch (error) {
                console.warn('[DatabaseHealthMonitor] Could not backup settings table:', error);
            }

            console.log('[DatabaseHealthMonitor] Database backup completed');
            return backup;

        } catch (error) {
            console.error('[DatabaseHealthMonitor] Database backup failed:', error);
            return null;
        }
    }

    /**
     * Restore database from backup data
     * @param {object} backupData - The backup data to restore
     */
    async restoreDatabase(backupData) {
        try {
            if (backupData.images && backupData.images.length > 0) {
                await db.images.bulkAdd(backupData.images);
                console.log(`[DatabaseHealthMonitor] Restored ${backupData.images.length} images`);
            }

            if (backupData.collection && backupData.collection.length > 0) {
                await db.collection.bulkAdd(backupData.collection);
                console.log(`[DatabaseHealthMonitor] Restored ${backupData.collection.length} collections`);
            }

            if (backupData.settings && backupData.settings.length > 0) {
                await db.settings.bulkAdd(backupData.settings);
                console.log(`[DatabaseHealthMonitor] Restored ${backupData.settings.length} settings`);
            }

            console.log('[DatabaseHealthMonitor] Database restoration completed');

        } catch (error) {
            console.error('[DatabaseHealthMonitor] Database restoration failed:', error);
            throw error;
        }
    }

    /**
     * Show corruption alert to the user
     */
    showCorruptionAlert() {
        // This would show a user-friendly notification
        // Implementation depends on the notification system used
        console.warn('[DatabaseHealthMonitor] Database corruption detected - user should be notified');
    }

    /**
     * Handle database errors from other parts of the system
     * @param {Error} error - The database error
     */
    async handleDatabaseError(error) {
        console.error('[DatabaseHealthMonitor] External database error reported:', error);

        this.consecutiveErrors++;
        this.lastError = error;

        // Check if this indicates corruption
        if (this.isCorruptionError(error)) {
            await this.handleCorruption(new DatabaseCorruptionError('External error indicates corruption', error));
        }
    }

    /**
     * Check if an error indicates database corruption
     * @param {Error} error - The error to check
     * @returns {boolean} True if error indicates corruption
     */
    isCorruptionError(error) {
        const corruptionPatterns = [
            'database disk image is malformed',
            'InvalidStateError',
            'UnknownError',
            'database or disk is full',
            'disk I/O error'
        ];

        const errorMessage = error.message.toLowerCase();
        return corruptionPatterns.some(pattern =>
            errorMessage.includes(pattern.toLowerCase())
        );
    }

    /**
     * Update check time metrics
     * @param {number} checkTime - Time taken for the check
     */
    updateCheckTimeMetrics(checkTime) {
        const alpha = 0.1; // Smoothing factor for exponential moving average
        this.metrics.averageCheckTime =
            (alpha * checkTime) + ((1 - alpha) * this.metrics.averageCheckTime);
    }

    /**
     * Get health monitor statistics
     * @returns {object} Health monitor metrics and status
     */
    getStats() {
        return {
            ...this.metrics,
            healthStatus: this.healthStatus,
            corruptionDetected: this.corruptionDetected,
            recoveryInProgress: this.recoveryInProgress,
            consecutiveErrors: this.consecutiveErrors,
            lastError: this.lastError?.message || null,
            lastHealthCheck: this.lastHealthCheck,
            successRate: this.metrics.totalHealthChecks > 0
                ? ((this.metrics.totalHealthChecks - this.metrics.failedHealthChecks) / this.metrics.totalHealthChecks) * 100
                : 0
        };
    }

    /**
     * Force a health check (bypass interval)
     * @returns {Promise<boolean>} True if database is healthy
     */
    async forceHealthCheck() {
        this.lastHealthCheck = 0; // Reset to force check
        return await this.checkHealth();
    }

    /**
     * Reset health monitor state
     */
    reset() {
        this.corruptionDetected = false;
        this.recoveryInProgress = false;
        this.healthStatus = 'unknown';
        this.lastError = null;
        this.consecutiveErrors = 0;
        this.lastHealthCheck = 0;

        console.log('[DatabaseHealthMonitor] State reset');
    }
}

// Custom error classes
class DatabaseError extends Error {
    constructor(message, originalError = null) {
        super(message);
        this.name = 'DatabaseError';
        this.originalError = originalError;
    }
}

class DatabaseCorruptionError extends DatabaseError {
    constructor(message, originalError = null) {
        super(message, originalError);
        this.name = 'DatabaseCorruptionError';
    }
}

// Create singleton instance
const databaseHealthMonitor = new DatabaseHealthMonitor();

export { DatabaseError, DatabaseCorruptionError };
export default databaseHealthMonitor;